import { supabase } from './supabaseClient';

const BUCKET_NAME = 'uploads';

/**
 * Загрузить файл в Supabase Storage
 * @param {File|Blob} file - Файл для загрузки
 * @param {string} path - Путь к файлу (например: 'users/{userId}/lessons/{lessonId}/banner.jpg')
 * @param {Object} options - Дополнительные опции
 * @returns {Promise<string>} - Публичный URL файла
 */
export const uploadFile = async (file, path, options = {}) => {
  try {
    const { cacheControl = '3600', upsert = false } = options;


    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(path, file, {
        cacheControl,
        upsert
      });

    if (error) {
      // Более понятные сообщения об ошибках
      if (error.message?.includes('Bucket not found') || error.statusCode === 400) {
        throw new Error(`Bucket '${BUCKET_NAME}' not found. Please create it in Supabase Dashboard → Storage → Create Bucket`);
      }
      if (error.message?.includes('permission denied') || error.statusCode === 403) {
        throw new Error('Permission denied. Please check RLS policies in Supabase.');
      }
      
      throw error;
    }

    // Получаем signed URL для файла (работает для приватных и публичных bucket)
    // Signed URL действует 1 год для фото профиля
    const { data: signedUrlData, error: signedUrlError } = await supabase.storage
      .from(BUCKET_NAME)
      .createSignedUrl(path, 31536000); // 1 год в секундах
    
    if (signedUrlError) {
      // Fallback: пробуем публичный URL (если bucket публичный)
      const { data: { publicUrl } } = supabase.storage
        .from(BUCKET_NAME)
        .getPublicUrl(path);
      return publicUrl;
    }
    
    return signedUrlData.signedUrl;
  } catch (error) {
    throw error;
  }
};

/**
 * Создать папку для студента в Storage
 * @param {string} studentId - ID студента
 * @param {string} teacherId - ID преподавателя (опционально, для структуры teachers/{teacherId}/students/{studentId})
 * @returns {Promise<boolean>}
 */
export const createStudentFolder = async (studentId, teacherId = null) => {
  try {
    console.log('createStudentFolder called:', { studentId, teacherId });
    
    // Проверяем, существует ли bucket
    // Примечание: listBuckets() может не работать из-за RLS, поэтому пробуем загрузить файл напрямую
    let bucketExists = false;
    try {
      const { data: buckets, error: bucketError } = await supabase.storage.listBuckets();
      if (!bucketError && buckets) {
        const uploadsBucket = buckets.find(b => b.id === BUCKET_NAME);
        if (uploadsBucket) {
          bucketExists = true;
          console.log(`✅ Bucket '${BUCKET_NAME}' found in list`);
        } else {
          console.warn(`⚠️ Bucket '${BUCKET_NAME}' not in list. Available buckets:`, buckets.map(b => b.id));
          console.warn('⚠️ Will try to upload anyway - bucket might exist but not be visible due to RLS');
        }
      } else if (bucketError) {
        console.warn('⚠️ Error checking buckets (might be RLS):', bucketError.message);
        console.warn('⚠️ Will try to upload anyway - bucket might exist but not be accessible via listBuckets()');
      }
    } catch (err) {
      console.warn('⚠️ Exception checking buckets:', err);
      console.warn('⚠️ Will try to upload anyway');
    }
    
    // Если bucket не найден в списке, все равно пробуем загрузить файл
    // (bucket может существовать, но быть недоступным через listBuckets из-за RLS)
    if (!bucketExists) {
      console.log('⚠️ Bucket not found in list, but will attempt upload anyway...');
    }
    
    // Определяем базовый путь для папки студента
    let basePath;
    if (teacherId) {
      // Структура: teachers/{teacherId}/students/{studentId}/
      basePath = `teachers/${teacherId}/students/${studentId}`;
    } else {
      // Структура: students/{studentId}/
      basePath = `students/${studentId}`;
    }

    console.log('Creating student folder structure:', basePath);

    // Проверяем, существует ли уже папка студента
    const { data: existingFiles, error: listError } = await supabase.storage
      .from(BUCKET_NAME)
      .list(basePath, {
        limit: 1,
        offset: 0
      });

    if (listError && listError.message !== 'not found') {
      console.warn('Error checking existing folder:', listError);
    }

    // Если папка уже существует и содержит файлы, пропускаем создание
    if (existingFiles && existingFiles.length > 0) {
      console.log(`✅ Student folder already exists: ${basePath}`);
      return true;
    }

    // Создаем структуру папок, используя минимальное PNG изображение (1x1 пиксель)
    // Это работает, так как image/* разрешен в bucket
    // Минимальный PNG (1x1 прозрачный пиксель) в base64
    const minimalPngBase64 = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';
    
    const folderMarkers = [
      { path: `${basePath}/profile/.folder-marker.png`, purpose: 'profile' },
      { path: `${basePath}/homework/.folder-marker.png`, purpose: 'homework' },
      { path: `${basePath}/attachments/.folder-marker.png`, purpose: 'attachments' }
    ];

    // Создаем файлы-маркеры из base64 PNG
    const createFolderMarker = (base64String) => {
      const byteCharacters = atob(base64String);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: 'image/png' });
    };

    // Загружаем маркеры для создания структуры папок
    const uploadPromises = folderMarkers.map(async ({ path, purpose }) => {
      console.log('Uploading folder marker:', path);
      try {
        const file = createFolderMarker(minimalPngBase64);
        console.log('File created:', { size: file.size, type: file.type, purpose });
        
        const { data, error } = await supabase.storage
          .from(BUCKET_NAME)
          .upload(path, file, {
            cacheControl: '3600',
            upsert: true, // Если файл уже существует, обновляем его
            contentType: 'image/png' // Используем PNG, так как image/* разрешен
          });
        
        if (error) {
          console.error(`❌ Error uploading ${path}:`, error);
          console.error('Error details:', {
            message: error.message,
            statusCode: error.statusCode,
            error: error.error,
            name: error.name
          });
          throw new Error(`Failed to upload ${path}: ${error.message || JSON.stringify(error)}`);
        }
        
        console.log(`✅ Successfully uploaded ${path}`, data);
        return { path, data, success: true };
      } catch (err) {
        console.error(`❌ Failed to upload ${path}:`, err);
        console.error('Exception details:', {
          message: err?.message,
          error: err?.error,
          path: err?.path
        });
        throw new Error(`Failed to upload ${path}: ${err?.message || JSON.stringify(err)}`);
      }
    });

    const results = await Promise.allSettled(uploadPromises);

    // Проверяем результаты
    const successful = results.filter(r => r.status === 'fulfilled');
    const errors = results
      .filter(result => result.status === 'rejected')
      .map(result => result.reason);

    console.log(`createStudentFolder: ${successful.length}/${results.length} folders created successfully`);
    console.log('Successful uploads:', successful.map(r => r.value?.path));
    console.log('Failed uploads:', errors.map(e => ({ path: e.path, error: e.error })));

    // Если есть ошибки, но не критичные (например, папка уже существует), продолжаем
    if (errors.length > 0) {
      console.warn('createStudentFolder: Some folders had errors:', errors);
      
      const criticalErrors = errors.filter(err => {
        const errorMessage = err?.message || err?.error?.message || JSON.stringify(err);
        return !errorMessage?.includes('already exists') && 
               !errorMessage?.includes('duplicate') &&
               !errorMessage?.includes('The resource already exists');
      });
      
      if (criticalErrors.length > 0) {
        console.error('createStudentFolder: Critical errors creating folders:', criticalErrors);
        // Не выбрасываем ошибку, так как папки могут быть созданы позже при загрузке файлов
      } else {
        console.log('createStudentFolder: All errors are non-critical (folders may already exist)');
      }
    }

    if (successful.length > 0) {
      console.log('✅ Student folder structure created successfully');
      
      // Проверяем, что файлы действительно доступны после создания
      console.log('Verifying created files...');
      const verifyPromises = successful.map(async (result) => {
        const path = result.value?.path;
        if (!path) return { path: 'unknown', verified: false };
        
        try {
          // Пробуем прочитать файл, чтобы убедиться, что он доступен
          const { data: fileData, error: readError } = await supabase.storage
            .from(BUCKET_NAME)
            .download(path);
          
          if (readError) {
            console.warn(`⚠️ Cannot read file after creation: ${path}`, readError);
            return { path, verified: false, error: readError };
          }
          
          console.log(`✅ File verified: ${path} (size: ${fileData?.size || 0} bytes)`);
          return { path, verified: true };
        } catch (err) {
          console.warn(`⚠️ Error verifying file: ${path}`, err);
          return { path, verified: false, error: err };
        }
      });
      
      // Небольшая задержка для обработки Supabase
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const verificationResults = await Promise.allSettled(verifyPromises);
      const verified = verificationResults.filter(r => r.status === 'fulfilled' && r.value?.verified);
      console.log(`Verified ${verified.length}/${successful.length} files after creation`);
      
      // Дополнительная проверка: список файлов в папке
      // Проверяем каждую подпапку отдельно
      const subfolders = ['profile', 'homework', 'attachments'];
      for (const subfolder of subfolders) {
        try {
          const subfolderPath = `${basePath}/${subfolder}`;
          const { data: listedFiles, error: listError } = await supabase.storage
            .from(BUCKET_NAME)
            .list(subfolderPath, {
              limit: 100,
              offset: 0,
              sortBy: { column: 'name', order: 'asc' }
            });
          
          if (listError) {
            console.warn(`⚠️ Cannot list files in ${subfolderPath}:`, listError);
          } else {
            const fileNames = listedFiles?.map(f => f.name) || [];
            console.log(`📁 Files in ${subfolderPath}:`, fileNames);
            if (fileNames.length > 0) {
              console.log(`✅ ${subfolder} folder contains ${fileNames.length} file(s)`);
            } else {
              console.warn(`⚠️ ${subfolder} folder appears empty after creation`);
            }
          }
        } catch (listErr) {
          console.warn(`⚠️ Error listing ${subfolder} folder contents:`, listErr);
        }
      }
      
      return true;
    } else {
      console.error('❌ No folders were created successfully');
      console.error('All uploads failed:', errors);
      // Возвращаем false, если ни одна папка не была создана
      return false;
    }
  } catch (error) {
    console.error('❌ Error creating student folder:', error);
    console.error('Error details:', {
      message: error.message,
      code: error.code,
      statusCode: error.statusCode,
      error: error.error
    });
    // Не выбрасываем ошибку, так как папки могут быть созданы позже при загрузке файлов
    return false;
  }
};

/**
 * Удалить файл из Supabase Storage
 * @param {string} path - Путь к файлу
 * @returns {Promise<boolean>}
 */
export const deleteFile = async (path) => {
  try {
    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .remove([path]);

    if (error) {
      console.error('Error deleting file:', error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteFile:', error);
    throw error;
  }
};

/**
 * Получить URL файла (signed URL для приватного bucket или public URL для публичного)
 * @param {string} path - Путь к файлу
 * @param {number} expiresIn - Время жизни signed URL в секундах (по умолчанию 1 час)
 * @returns {Promise<string>} - URL файла
 */
export const getFileUrl = async (path, expiresIn = 3600) => {
  try {
    // Пробуем создать signed URL (для приватного bucket)
    const { data: signedUrlData, error: signedUrlError } = await supabase.storage
      .from(BUCKET_NAME)
      .createSignedUrl(path, expiresIn);
    
    if (!signedUrlError && signedUrlData) {
      return signedUrlData.signedUrl;
    }
    
    // Fallback: используем публичный URL (если bucket публичный)
    const { data: { publicUrl } } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(path);
    
    return publicUrl;
  } catch (error) {
    console.error('Error getting file URL:', error);
    // Fallback: возвращаем публичный URL
    const { data: { publicUrl } } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(path);
    return publicUrl;
  }
};

/**
 * Получить публичный URL файла (устаревший метод, используйте getFileUrl)
 * @param {string} path - Путь к файлу
 * @returns {string} - Публичный URL
 */
export const getPublicUrl = (path) => {
  const { data: { publicUrl } } = supabase.storage
    .from(BUCKET_NAME)
    .getPublicUrl(path);

  return publicUrl;
};

/**
 * Конвертировать base64 строку в File объект
 * @param {string} base64 - Base64 строка (data:image/... или data:video/...)
 * @param {string} filename - Имя файла
 * @returns {File} - File объект
 */
export const base64ToFile = (base64, filename) => {
  try {
    // Извлекаем MIME type и данные
    const arr = base64.split(',');
    if (arr.length < 2) {
      throw new Error('Invalid base64 string');
    }

    const mimeMatch = arr[0].match(/:(.*?);/);
    if (!mimeMatch) {
      throw new Error('Could not extract MIME type from base64 string');
    }

    const mime = mimeMatch[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  } catch (error) {
    console.error('Error converting base64 to file:', error);
    throw error;
  }
};

const MIME_EXT = {
  'image/jpeg': 'jpg', 'image/png': 'png', 'image/gif': 'gif', 'image/webp': 'webp',
  'audio/mpeg': 'mp3', 'audio/mp3': 'mp3', 'audio/wav': 'wav', 'audio/webm': 'webm', 'audio/ogg': 'ogg', 'audio/mp4': 'm4a',
  'video/mp4': 'mp4', 'video/webm': 'webm', 'video/ogg': 'ogv'
};

/**
 * Мигрировать base64 медиа (изображение/аудио/видео) в Storage
 * @param {string} base64 - Base64 строка (data:image/..., data:audio/..., data:video/...)
 * @param {string} userId - ID пользователя
 * @param {string} lessonId - ID урока (или 'new' для нового урока)
 * @param {string} type - Тип файла ('banner', 'doc-banner', 'homework-banner', 'content-image', 'content-audio', 'content-video')
 * @param {string} extension - Расширение (опционально, по умолчанию из MIME)
 * @returns {Promise<string>} - URL файла в Storage
 */
export const migrateBase64ToStorage = async (
  base64,
  userId,
  lessonId,
  type = 'banner',
  extension
) => {
  try {
    if (!base64 || typeof base64 !== 'string') return base64;
    const isImage = base64.startsWith('data:image/');
    const isAudio = base64.startsWith('data:audio/');
    const isVideo = base64.startsWith('data:video/');
    if (!isImage && !isAudio && !isVideo) return base64;

    const mimeMatch = base64.match(/^data:([^;]+);/);
    const mime = mimeMatch ? mimeMatch[1].toLowerCase() : '';
    const ext = extension || MIME_EXT[mime] || (isImage ? 'jpg' : isAudio ? 'webm' : 'mp4');
    const fileType = isImage ? 'image' : isAudio ? 'audio' : 'video';
    const filename = `${type}-${Date.now()}.${ext}`;
    const file = base64ToFile(base64, filename);

    const subdir = type.startsWith('content-') ? fileType : 'banners';
    const path = `users/${userId}/lessons/${lessonId}/${subdir}/${filename}`;

    const url = await uploadFile(file, path);
    console.log(`✅ Migrated ${type} to Storage: ${path}`);
    return url;
  } catch (error) {
    console.error(`Error migrating base64 to Storage (${type}):`, error);
    return base64;
  }
};

/**
 * Upload a single media src (blob: or data: URL) to Supabase Storage.
 * Returns the signed URL, or the original src on failure.
 */
export const migrateMediaSrcToStorage = async (src, userId, lessonId) => {
  if (!src || typeof src !== 'string') return src;
  if (!src.startsWith('blob:') && !src.startsWith('data:')) return src;

  try {
    let blob;
    let mimeType;

    if (src.startsWith('blob:')) {
      const response = await fetch(src);
      blob = await response.blob();
      mimeType = blob.type;
    } else {
      // data: URL
      const mimeMatch = src.match(/^data:([^;]+);/);
      mimeType = mimeMatch ? mimeMatch[1].toLowerCase() : 'application/octet-stream';
      blob = base64ToFile(src, 'tmp');
      mimeType = blob.type || mimeType;
    }

    const ext = MIME_EXT[mimeType] || 'bin';
    const isAudio = mimeType.startsWith('audio/');
    const isImage = mimeType.startsWith('image/');
    const subdir = isImage ? 'image' : isAudio ? 'audio' : 'video';
    const typeLabel = isImage ? 'content-image' : isAudio ? 'content-audio' : 'content-video';
    const filename = `${typeLabel}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext}`;
    const path = `users/${userId}/lessons/${lessonId}/${subdir}/${filename}`;
    const file = new File([blob], filename, { type: mimeType });
    return await uploadFile(file, path);
  } catch (e) {
    console.warn('migrateMediaSrcToStorage: failed to migrate', src.slice(0, 40), e);
    return src;
  }
};

/**
 * Мигрировать все base64 изображения в контенте урока
 * @param {Object} content - Контент урока (может содержать base64 изображения)
 * @param {string} userId - ID пользователя
 * @param {string} lessonId - ID урока
 * @returns {Promise<Object>} - Контент с мигрированными URL
 */
export const migrateContentBase64 = async (content, userId, lessonId) => {
  if (!content) return content;

  try {
    // Если content - это строка HTML, ищем base64 и blob медиа
    if (typeof content === 'string') {
      let migratedContent = content;

      // 1. base64 data: URLs
      const base64Regex = /src=["'](data:(?:image|audio|video)\/[^"']+)["']/g;
      const base64Matches = [];
      let match;
      while ((match = base64Regex.exec(content)) !== null) {
        base64Matches.push(match[1]);
      }
      const typeFor = (dataUrl) => {
        if (dataUrl.startsWith('data:image/')) return 'content-image';
        if (dataUrl.startsWith('data:audio/')) return 'content-audio';
        if (dataUrl.startsWith('data:video/')) return 'content-video';
        return 'content-image';
      };
      const base64Urls = await Promise.all(
        base64Matches.map((b64) => migrateBase64ToStorage(b64, userId, lessonId, typeFor(b64)))
      );
      for (let i = 0; i < base64Matches.length; i++) {
        migratedContent = migratedContent.replace(base64Matches[i], base64Urls[i]);
      }

      // 2. blob: URLs (temporary, only valid in current session)
      const blobRegex = /src=["'](blob:[^"']+)["']/g;
      const blobMatches = [];
      while ((match = blobRegex.exec(migratedContent)) !== null) {
        blobMatches.push(match[1]);
      }
      const blobUrls = await Promise.all(
        blobMatches.map((blobUrl) => migrateMediaSrcToStorage(blobUrl, userId, lessonId))
      );
      for (let i = 0; i < blobMatches.length; i++) {
        migratedContent = migratedContent.replace(blobMatches[i], blobUrls[i]);
      }

      return migratedContent;
    }

    // Если content - это объект (JSONB), рекурсивно обрабатываем
    if (typeof content === 'object') {
      const migrated = { ...content };

      // Обрабатываем bannerImage
      if (migrated.bannerImage && typeof migrated.bannerImage === 'string') {
        migrated.bannerImage = await migrateBase64ToStorage(
          migrated.bannerImage,
          userId,
          lessonId,
          'banner'
        );
      }

      // Обрабатываем docBanners
      if (migrated.docBanners && typeof migrated.docBanners === 'object') {
        for (const key in migrated.docBanners) {
          if (migrated.docBanners[key] && typeof migrated.docBanners[key] === 'string') {
            migrated.docBanners[key] = await migrateBase64ToStorage(
              migrated.docBanners[key],
              userId,
              lessonId,
              `doc-banner-${key}`
            );
          }
        }
      }

      // Обрабатываем homeworkBanners
      if (migrated.homeworkBanners && typeof migrated.homeworkBanners === 'object') {
        for (const key in migrated.homeworkBanners) {
          if (migrated.homeworkBanners[key] && typeof migrated.homeworkBanners[key] === 'string') {
            migrated.homeworkBanners[key] = await migrateBase64ToStorage(
              migrated.homeworkBanners[key],
              userId,
              lessonId,
              `homework-banner-${key}`
            );
          }
        }
      }

      // Рекурсивно обрабатываем editorContents
      if (Array.isArray(migrated.editorContents)) {
        for (const editor of migrated.editorContents) {
          if (editor.content && typeof editor.content === 'string') {
            editor.content = await migrateContentBase64(
              editor.content,
              userId,
              lessonId
            );
          }
        }
      }

      return migrated;
    }

    return content;
  } catch (error) {
    console.error('Error migrating content base64:', error);
    return content;
  }
};

